import React, { useState, useRef } from "react";

export default function Contact() {
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // "ok" | "error" | null
  const [errors, setErrors] = useState({});
  const [serviceChoice, setServiceChoice] = useState(""); // select value

  // refs to focus first error
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const serviceRef = useRef(null);
  const serviceOtherRef = useRef(null);
  const messageRef = useRef(null);

  function refId() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `REQ-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  }

  // helpers
  const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  function validate(form) {
    const vals = {
      name: (form.name?.value || "").trim(),
      email: (form.email?.value || "").trim(),
      phone: (form.phone?.value || "").trim(),
      service: (form.service?.value || "").trim(),
      service_other: (form.service_other?.value || "").trim(),
      message: (form.message?.value || "").trim(),
    };

    const e = {};
    if (!vals.name) e.name = "Bitte geben Sie Ihren Namen ein.";
    if (!vals.email) e.email = "Bitte geben Sie Ihre E-Mail ein.";
    else if (!emailOk(vals.email)) e.email = "Bitte geben Sie eine gültige E-Mail ein.";
    if (!vals.service) e.service = "Bitte wählen Sie eine Dienstleistung.";
    if (vals.service === "Andere" && !vals.service_other) {
      e.service_other = "Bitte geben Sie die gewünschte Dienstleistung ein.";
    }
    if (!vals.message) e.message = "Bitte geben Sie Ihre Nachricht ein.";
    else if (vals.message.length < 10) e.message = "Ihre Nachricht sollte mindestens 10 Zeichen enthalten.";

    return { e, vals };
  }

  function scrollToFirstError(e) {
    const order = [
      { key: "name", ref: nameRef },
      { key: "email", ref: emailRef },
      { key: "service", ref: serviceRef },
      { key: "service_other", ref: serviceOtherRef },
      { key: "message", ref: messageRef },
      { key: "phone", ref: phoneRef },
    ];
    for (const item of order) {
      if (e[item.key]) {
        item.ref.current?.focus({ preventScroll: true });
        item.ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        break;
      }
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (sending) return;

    setStatus(null);

    const form = e.currentTarget;
    const { e: fieldErrors, vals } = validate(form);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length) {
      scrollToFirstError(fieldErrors);
      return;
    }

    setSending(true);

    // Build payload
    const data = new FormData(form);
    // if "Andere", replace service with custom text
    if (vals.service === "Andere") {
      data.set("service", vals.service_other);
    }

    // Required Web3Forms fields
    data.set("access_key", "1c684971-cc29-4f46-8b63-b32722d33028"); // your key ( Change if i need to change)
    data.set("subject", `Neue Anfrage von GVSI Clean Care • ${refId()}`);
    data.set("from_name", "GVSI Clean Care ");

    // Honeypot
    if (data.get("botcheck")) {
      setSending(false);
      setStatus("ok");
      form.reset();
      setServiceChoice("");
      setErrors({});
      return;
    }

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: data,
      });
      const json = await res.json();
      if (json.success) {
        setStatus("ok");
        setErrors({});
        form.reset();
        setServiceChoice("");
      } else {
        console.error("[Web3Forms] Error response:", json);
        setStatus("error");
      }
    } catch (err) {
      console.error("[Web3Forms] Network error:", err);
      setStatus("error");
    } finally {
      setSending(false);
    }
  }

  // Styling helpers
  const inputWrap = "relative mt-1";
  const leftIcon = "pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400";
  const inputBase =
    "w-full rounded-lg border pl-10 pr-4 py-2.5 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2";
  const inputOk = "border-gray-300 focus:ring-green-500";
  const inputErr = "border-red-300 focus:ring-red-500";

  const selectBase =
    "w-full appearance-none rounded-lg border pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white";
  const textAreaBase =
    "w-full rounded-lg border pr-4 py-3 text-sm leading-6 placeholder:text-gray-400 focus:outline-none focus:ring-2 pl-10";

  return (
    <section id="contact" className="bg-white py-16 sm:py-20 " >
      {/* subtle top accent */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500" />

      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Kontaktieren Sie uns
          </h1>
          <p className="mt-3 text-gray-600">
            Geschäftsführerin: Armanda Gashi (CEO) – Standort: Rosenheim, 83024, Deutschland.
            Wir sind Ihr zuverlässiger Partner für professionelle Reinigungsdienste.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-3">
          {/* Info */}
          <aside className="lg:col-span-1">
            <div className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900">Unsere Kontaktdaten</h2>

              <ul className="mt-5 space-y-4 text-sm text-gray-600">
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-green-50 text-green-600">
                    {/* mail */}
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M4 6h16v12H4z" />
                      <path d="M22 6l-10 7L2 6" />
                    </svg>
                  </span>
                  <div>
                    <p className="font-medium text-gray-900">E-Mail</p>
                    <a href="mailto:info@gvsiclean.de" className="text-green-600 hover:underline break-all">
                      info@gvsiclean.de
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-green-50 text-green-600">
                    {/* phone */}
                    <svg
                      viewBox="0 0 24 24"
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M22 16.92v3a2 2 0 01-2.18 2
           19.79 19.79 0 01-8.63-3.07
           19.5 19.5 0 01-6-6
           19.79 19.79 0 01-3.07-8.63A2 2 0 014.92 2h3a2 2 0 012 1.72
           12.05 12.05 0 00.65 2.57 2 2 0 01-.45 2.11L9 9
           a16 16 0 006 6l.6-.6a2 2 0 012.11-.45
           12.05 12.05 0 002.57.65A2 2 0 0122 16.92z"
                      />
                    </svg>
                  </span>
                  <div>
                    <p className="font-medium text-gray-900">Phone</p>
                    <a href="tel:+491607635410" className="text-green-600 hover:underline">
                      +49 160 7635410
                    </a>
                  </div>
                </li>



                <li className="flex items-start ">
                  <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-green-50 text-green-600">
                    {/* location */}
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </span>
                  <div>
                    <p className="font-medium text-gray-900">Adresse</p>
                    <p>Rosenheim, 83024, Deutschland</p>
                  </div>
                </li>

                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-green-50 text-green-600">
                    {/* clock */}
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                  </span>
                  <div>
                    <p className="font-medium text-gray-900">Arbeitszeiten</p>
                    <p>Montag - Sonntag: </p>
                    <p>08:00 - 20:00</p>
                  </div>
                </li>
              </ul>

              <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600">
                <div className="flex items-center gap-2 font-medium text-gray-800">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4" />
                    <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  DSGVO-konform
                </div>
                <p className="mt-3 font-light text-gray-900">Wir verwenden Ihre Daten ausschließlich zur Bearbeitung Ihrer Anfrage.</p>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-2">
            <div className="rounded-xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm">
              <form onSubmit={onSubmit} noValidate className="space-y-6">
                {/* Honeypot */}
                <input
                  type="checkbox"
                  name="botcheck"
                  tabIndex="-1"
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                />

                <div className="grid grid-cols-1 gap-5 sm:gap-6 sm:grid-cols-2">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Ihr Name *
                    </label>
                    <div className={inputWrap}>
                      <span className={leftIcon}>
                        {/* user */}
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <circle cx="12" cy="8" r="4" />
                          <path d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6" />
                        </svg>
                      </span>
                      <input
                        id="name"
                        name="name"
                        ref={nameRef}
                        required
                        placeholder="Ihr Name"
                        className={`${inputBase} ${errors.name ? inputErr : inputOk}`}
                      />
                    </div>
                    {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      E-Mail *
                    </label>
                    <div className={inputWrap}>
                      <span className={leftIcon}>
                        {/* at */}
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M16 8a6 6 0 10-2.2 11.6" />
                          <circle cx="12" cy="12" r="2.5" />
                          <path d="M16 12v1a3 3 0 006 0v-1" />
                        </svg>
                      </span>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        ref={emailRef}
                        required
                        placeholder="you@example.com"
                        className={`${inputBase} ${errors.email ? inputErr : inputOk}`}
                      />
                    </div>
                    {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                  </div>

                  {/* Phone (optional) */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Telefon
                    </label>
                    <div className={inputWrap}>
                      <span className={leftIcon}>
                        {/* phone */}
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.08 4.18 2 2 0 014.06 2h3a2 2 0 012 1.72c.12.9.33 1.78.62 2.63a2 2 0 01-.45 2.11L8.1 9.9a16 16 0 006 6l1.44-1.13a2 2 0 012.11-.45c.85.29 1.73.5 2.63.62A2 2 0 0122 16.92z" />
                        </svg>
                      </span>
                      <input
                        id="phone"
                        type="tel"
                        name="phone"
                        ref={phoneRef}
                        placeholder="+49 160 7635410"
                        className={`${inputBase} ${inputOk}`}
                      />
                    </div>
                  </div>

                  {/* Service (select) */}
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700">
                      Gewünschte Dienstleistung *
                    </label>
                    <div className="relative mt-1">
                      <span className={leftIcon}>
                        {/* briefcase */}
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M4 7h16a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V9a2 2 0 012-2z" />
                          <path d="M16 7V6a2 2 0 00-2-2h-4a2 2 0 00-2 2v1" />
                        </svg>
                      </span>
                      <select
                        id="service"
                        name="service"
                        ref={serviceRef}
                        value={serviceChoice}
                        onChange={(ev) => setServiceChoice(ev.target.value)}
                        className={`${selectBase} ${errors.service ? inputErr : inputOk}`}
                        required
                      >
                        <option value="">Bitte auswählen …</option>
                        <option>Treppenhausreinigung</option>
                        <option>Büroreinigung</option>
                        <option>Fensterreinigung</option>
                        <option>Bauendreinigung</option>
                        <option>Grundreinigung</option>
                        <option>Hausmeisterdienst</option>
                        <option>Winterdienst</option>
                        <option>Grünpflege</option>
                        <option>Umzugsreinigung</option>
                        <option>Gastronomiereinigung</option>
                        <option>Desinfektionsreinigung</option>
                        <option>Solaranlagen-Reinigung</option>
                        <option>Industriereinigung</option>
                        <option>Andere</option>
                      </select>

                      {/* chevron */}
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </div>
                    {errors.service && <p className="mt-1 text-xs text-red-600">{errors.service}</p>}
                  </div>
                </div>

                {/* Service "Andere" free text */}
                {serviceChoice === "Andere" && (
                  <div>
                    <label htmlFor="service_other" className="block text-sm font-medium text-gray-700">
                      Andere Dienstleistung *
                    </label>
                    <div className={inputWrap}>
                      <span className={leftIcon}>
                        {/* pencil */}
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
                        </svg>
                      </span>
                      <input
                        id="service_other"
                        name="service_other"
                        ref={serviceOtherRef}
                        placeholder="Beschreiben Sie die gewünschte Dienstleistung…"
                        className={`${inputBase} ${errors.service_other ? inputErr : inputOk}`}
                      />
                    </div>
                    {errors.service_other && <p className="mt-1 text-xs text-red-600">{errors.service_other}</p>}
                  </div>
                )}

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Ihre Nachricht *
                  </label>
                  <div className="relative mt-1">
                    <span className={leftIcon.replace("pl-3", "top-3 left-3")}>
                      {/* chat */}
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M21 15a4 4 0 01-4 4H8l-5 3V7a4 4 0 014-4h10a4 4 0 014 4z" />
                      </svg>
                    </span>
                    <textarea
                      id="message"
                      name="message"
                      ref={messageRef}
                      rows="5"
                      required
                      placeholder="Beschreiben Sie Ihr Anliegen..."
                      className={`${textAreaBase} ${errors.message ? inputErr : inputOk}`}
                    />
                  </div>
                  {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
                </div>

                {/* Footer actions */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-gray-500 order-2 sm:order-1">
                    Beim Absenden bleibt diese Seite geöffnet. Wir zeigen Ihnen hier den Status.
                  </p>
                  <button
                    type="submit"
                    disabled={sending}
                    className="order-1 sm:order-2 inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-medium text-white shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-60 disabled:cursor-not-allowed transition"
                  >
                    {sending && (
                      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4A4 4 0 004 12z"></path>
                      </svg>
                    )}
                    {sending ? "Sende…" : "Senden"}
                  </button>
                </div>

                {/* Status banners */}
                {status === "ok" && (
                  <div role="alert" className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                    <svg viewBox="0 0 24 24" className="h-5 w-5 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4" />
                      <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>Danke! Ihre Nachricht wurde gesendet. Bitte prüfen Sie Ihr Postfach.</p>
                  </div>
                )}
                {status === "error" && (
                  <div role="alert" className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <svg viewBox="0 0 24 24" className="h-5 w-5 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 9v4" />
                      <path d="M12 17h.01" />
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <p>Leider gab es ein Problem beim Senden. Bitte versuchen Sie es später erneut.</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
